import { useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Upload, Image as ImageIcon, X, CheckCircle2, Newspaper } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SubmitPressReleasePage() {
  const [f, setF] = useState({
    title: "", subtitle: "", content: "",
    name: "", company_name: "", email: "", phone: "",
  });
  const [file, setFile] = useState(null); // File object
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const onPick = (e) => {
    const chosen = e.target.files?.[0];
    if (!chosen) return;
    const ext = (chosen.name.split(".").pop() || "").toLowerCase();
    if (!["jpg", "jpeg", "png", "webp"].includes(ext)) {
      toast.error("Only JPG, PNG, or WEBP images are allowed");
      return;
    }
    if (chosen.size > 2 * 1024 * 1024) {
      toast.error("Image too large. Max 2 MB.");
      return;
    }
    setFile(chosen);
    const url = URL.createObjectURL(chosen);
    setPreview(url);
  };

  const removeImg = () => { setFile(null); setPreview(""); if (fileRef.current) fileRef.current.value = ""; };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.content.trim() || !f.name.trim() || !f.company_name.trim() || !f.email.trim()) {
      toast.error("Please fill all required fields");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      const res = await fetch(`${API}/api/press-releases/submit`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      setDone(true);
      toast.success("Submitted! Editors will review shortly.");
    } catch (err) {
      toast.error(err.message || "Submission failed");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16" data-testid="press-submit-done">
        <div className="card-soft p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8 text-emerald-700"/></div>
          <h1 className="font-editorial text-3xl font-bold text-slate-900 mb-2">Press release received</h1>
          <p className="text-slate-600">Thanks {f.name}. Our editors will review your submission from <span className="font-semibold text-sky-700">{f.company_name}</span> and get back to you at <span className="font-semibold text-sky-700">{f.email}</span> if we need clarifications. Approved releases are typically published within 24–48 hours.</p>
          <button onClick={() => { setDone(false); setF({ title:"", subtitle:"", content:"", name:"", company_name:"", email:"", phone:"" }); removeImg(); }} className="mt-6 px-5 py-2.5 rounded-full bg-sky-700 text-white text-sm font-semibold" data-testid="press-submit-another">Submit another release</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="press-submit-page">
      <div className="flex items-center gap-2 text-sky-700 mb-2"><Newspaper className="w-4 h-4"/><span className="overline">Submit press release</span></div>
      <h1 className="font-editorial text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">Share your story with India's education community.</h1>
      <p className="text-slate-600 mt-3 max-w-2xl">Universities, schools, EdTech companies, publishers and event organisers: submit your press release below. Our editors review every submission and publish approved stories on EduTime Today.</p>

      <form onSubmit={submit} className="card-soft p-8 mt-8 space-y-5">
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Headline <span className="text-red-500">*</span></label>
          <input required value={f.title} onChange={e => setF({...f, title: e.target.value})} placeholder="e.g., XYZ University Launches New AI Programme" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 font-editorial text-lg" data-testid="press-title"/>
        </div>
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Subtitle (optional)</label>
          <input value={f.subtitle} onChange={e => setF({...f, subtitle: e.target.value})} placeholder="Short summary in one line" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="press-subtitle"/>
        </div>
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Full press release <span className="text-red-500">*</span></label>
          <textarea required rows={10} value={f.content} onChange={e => setF({...f, content: e.target.value})} placeholder="Paste your press release here. Plain text or basic HTML is fine." className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm" data-testid="press-content"/>
        </div>

        {/* Image upload */}
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Photograph (optional, JPG/PNG/WEBP · max 2 MB)</label>
          {preview ? (
            <div className="relative w-56 h-40 rounded-xl overflow-hidden border border-slate-200" data-testid="press-image-preview">
              <img src={preview} alt="" className="w-full h-full object-cover"/>
              <button type="button" onClick={removeImg} className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 shadow" data-testid="press-image-remove"><X className="w-3.5 h-3.5"/></button>
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} className="w-56 h-40 rounded-xl border-2 border-dashed border-slate-200 hover:border-sky-400 flex flex-col items-center justify-center gap-2 text-slate-500 hover:text-sky-700 text-sm" data-testid="press-image-picker">
              <ImageIcon className="w-6 h-6"/>Click to upload<span className="text-[10px] text-slate-400">Max 2 MB</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPick} data-testid="press-image-input"/>
        </div>

        <div className="overline text-slate-500 mt-4">Contact details</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input required value={f.name} onChange={e => setF({...f, name: e.target.value})} placeholder="Your full name *" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="press-name"/>
          <input required value={f.company_name} onChange={e => setF({...f, company_name: e.target.value})} placeholder="Company / Institution name *" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="press-company"/>
          <input required type="email" value={f.email} onChange={e => setF({...f, email: e.target.value})} placeholder="Email *" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="press-email"/>
          <input type="tel" value={f.phone} onChange={e => setF({...f, phone: e.target.value})} placeholder="Phone / Telephone" className="px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-sky-400" data-testid="press-phone"/>
        </div>

        <div className="pt-2 border-t border-slate-100 flex items-center gap-2">
          <button disabled={busy} className="px-6 py-3 rounded-full bg-sky-700 hover:bg-sky-800 text-white font-semibold text-sm flex items-center gap-1 disabled:opacity-60" data-testid="press-submit-btn">
            <Send className="w-4 h-4"/>{busy ? "Submitting…" : "Submit for review"}
          </button>
          <span className="text-xs text-slate-500 ml-2">Submissions are reviewed by our editors before publishing.</span>
        </div>
      </form>
    </div>
  );
}
